import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { observer } from 'mobx-react-lite';
import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import {
    AttachmentLocalComputerFilePicker,
    ComposeUniqueState,
    FilePickerAction,
    FileSelectionFilter,
    IAttachmentLocalComputerFilePicker,
} from 'owa-attachment-filepicker';
import {
    AttachmentMultiMenuEntry,
    attachmentMultiMenuEntry,
    GetCreateAttachmentHandlerFunc,
} from 'owa-attachment-multi-menu-actions';
import {
    AttachmentSuggestionsMenuItems,
    lazyGetAttachmentSuggestionsMenuItems,
    lazyGetAttachmentSuggestionsTraceId,
    ReplyWithAttachmentSuggestion,
} from 'owa-attachment-suggestions';
import { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { LazyInsertMenuItem } from 'owa-compose-ribbon';
import { ControlIcons } from 'owa-control-icons';
import { differenceInMilliseconds, OwaDate, owaDate } from 'owa-datetime';
import { focusToEditor } from 'owa-editor';
import { lazyCloseChecker, lazyOpenChecker } from 'owa-editor-acc-checker';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    DisplayOption,
    FilterableCommandBar,
    FilterableMenuItem,
    FilterableMenuItemArgs,
} from 'owa-filterable-menu';
import { lazyInsertLinks } from 'owa-insert-link';
import loc from 'owa-localize';
import { accCheckerMenuItemText } from 'owa-locstrings/lib/strings/acccheckermenuitemtext.locstring.json';
import { attachTooltip } from 'owa-locstrings/lib/strings/attachtooltip.locstring.json';
import { composeDiscardButton } from 'owa-locstrings/lib/strings/composediscardbutton.locstring.json';
import {
    composeProtectButton,
    composeSetPermissionDropDown,
} from 'owa-locstrings/lib/strings/composeprotectbutton.locstring.json';
import { composeSendButton } from 'owa-locstrings/lib/strings/composesendbutton.locstring.json';
import { highImportanceMenuText } from 'owa-locstrings/lib/strings/highimportancemenutext.locstring.json';
import { insertSignature as insertSignature_1 } from 'owa-locstrings/lib/strings/insertsignature.locstring.json';
import { lowImportanceMenuText } from 'owa-locstrings/lib/strings/lowimportancemenutext.locstring.json';
import { normalImportanceMenuText } from 'owa-locstrings/lib/strings/normalimportancemenutext.locstring.json';
import { popoutsOpenInNewWindow } from 'owa-locstrings/lib/strings/popoutsopeninnewwindow.locstring.json';
import { saveDraft } from 'owa-locstrings/lib/strings/savedraft.locstring.json';
import { sending } from 'owa-locstrings/lib/strings/sending.locstring.json';
import { sensitivityMenuText } from 'owa-locstrings/lib/strings/sensitivitymenutext.locstring.json';
import { setImportance as setImportance_1 } from 'owa-locstrings/lib/strings/setimportance.locstring.json';
import { showFrom } from 'owa-locstrings/lib/strings/showfrom.locstring.json';
import { showMessageOptions } from 'owa-locstrings/lib/strings/showmessageoptions.locstring.json';
import { switchToPlainTextMenuItem } from 'owa-locstrings/lib/strings/switchtoplaintextmenuitem.locstring.json';
import clickAttachButton from 'owa-mail-compose-actions/lib/actions/clickAttachButton';
import discardCompose from 'owa-mail-compose-actions/lib/actions/discardCompose';
import setImportance from 'owa-mail-compose-actions/lib/actions/setImportance';
import switchBodytype from 'owa-mail-compose-actions/lib/actions/switchBodyType';
import toggleFrom from 'owa-mail-compose-actions/lib/actions/toggleFrom';
import trySaveMessage from 'owa-mail-compose-actions/lib/actions/trySaveMessage';
import getComposeUniqueState from 'owa-mail-compose-actions/lib/utils/getComposeUniqueState';
import getCurrentFromAddress from 'owa-mail-compose-actions/lib/utils/getFromAddressFromRecipientWell';
import getSuggestedAttachmentMessageFromComposeViewState from 'owa-mail-compose-actions/lib/utils/getSuggestedAttachmentMessageFromComposeViewState';
import insertSignature from 'owa-mail-compose-actions/lib/utils/insertSignature';
import popoutCompose from 'owa-mail-compose-actions/lib/utils/popoutCompose';
import protectMessageAndRemoveAddin from 'owa-mail-compose-actions/lib/utils/protectMessageAndRemoveAddin';
import { ComposeViewState, UIEnabledState } from 'owa-mail-compose-store';
import isPublicFolderComposeViewState from 'owa-mail-compose-store/lib/utils/isPublicFolderComposeViewState';
import { showMessageOptionsDialog } from 'owa-mail-message-options-view';
import { protectionStore } from 'owa-mail-protection/lib/store/protectionStore';
import {
    ENCRYPT_DNF_GUID,
    ENCRYPT_GUID,
    IRM_DEFAULT_GUID,
} from 'owa-mail-protection/lib/store/schema/rms/EncryptionGUID';
import isCLPEnabled from 'owa-mail-protection/lib/utils/clp/isCLPEnabled';
import shouldDisableEncryptionButton from 'owa-mail-protection/lib/utils/rms/shouldDisableEncryptionButton';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import isRoamingSignatureEnabled from 'owa-roaming-signature-option/lib/utils/isRoamingSignatureEnabled';
import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { isConsumer } from 'owa-session-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { SurfaceActionIcons } from 'owa-surface-action-icons';
import isMessageOptionsEnabled from 'owa-surface-actions/lib/utils/isMessageOptionsEnabled';
import { isDeepLink } from 'owa-url';
import * as React from 'react';
import send from '../commands/send';
import {
    composeEllipsisAriaLabelText,
    composePostButton,
    encryptDisableForwardingMenuText,
    encryptMenuText,
    hideFrom,
    switchToHTMLMenuItem,
} from '../strings.locstring.json';
import AttachmentMenuEntrySource from '../types/AttachmentMenuEntrySource';
import { AttachmentMultiMenuMap } from '../types/AttachmentMultiMenuMap';
import { onAttachmentClicked } from '../utils/attachButtonMenuItemBuilderUtils';
import { aFPCreateAttachmentHandler } from '../utils/createAttachmentHandler';
import { getAttachAsClassicMenuItems } from '../utils/getAttachAsClassicMenuItems';
import {
    getDefaultButtonsList,
    getSmimeAttachButtonsList,
} from '../utils/getAttachmentCommandItems';
import { getCreateClassicAttachmentHandler } from '../utils/getCreateClassicAttachmentHandler';
import {
    getEncryptionDropDownSubMenu,
    getIRMTemplatesDropDown,
} from '../utils/getEncryptionDropDownSubMenu';
import { getInsertLinkMenuItems } from '../utils/getInsertLinkMenuItems';
import getInsertSignatureDropdown from '../utils/getInsertSignatureDropdown';
import getSensitivitySubMenu from '../utils/getSensitivitySubMenu';
import isApprovalResponse from '../utils/isApprovalResponse';
import isEncryptionTemplateAvailable from '../utils/isEncryptionTemplateAvailable';
import logAttachButtonClickedDatapoint from '../utils/logAttachButtonClickedDatapoint';
import { upsellAndInsertLinks } from '../utils/upsellAndInsertLinks';

const styles = require('./ComposeCommandingBar.scss');
const classNames = require('classnames');

export interface ComposeCommandingBarProps {
    composeViewState: ComposeViewState;
    // padding between the leftmost custom item and the rest of the command bar actions (still present if there are no custom items)
    leftPadding?: number;
    renderCustomItem?: () => JSX.Element;
}

export default observer(function ComposeCommandingBar(props: ComposeCommandingBarProps) {
    const targetWindow = React.useContext(ProjectionContext);
    const attachmentLocalComputerFilePicker = React.useRef<
        React.RefObject<IAttachmentLocalComputerFilePicker>
    >(React.createRef());
    const attachmentLocalComputerToCloudFilePicker = React.useRef<
        React.RefObject<IAttachmentLocalComputerFilePicker>
    >(React.createRef());
    const attachmentLocalFileToLinkPicker = React.useRef<
        React.RefObject<IAttachmentLocalComputerFilePicker>
    >(React.createRef());
    const attachmentLocalComputerFilePickerWithInsertLink = React.useRef<
        React.RefObject<IAttachmentLocalComputerFilePicker>
    >(React.createRef());
    const attachmentSuggestionsMenuItems = React.useRef<AttachmentSuggestionsMenuItems>();
    if (!attachmentSuggestionsMenuItems.current) {
        attachmentSuggestionsMenuItems.current = lazyGetAttachmentSuggestionsMenuItems.tryImportForRender()?.(
            true /* isMail */
        );
    }
    const commandingBarItems = useComputed((): FilterableMenuItem[] => {
        const { composeViewState } = props;
        if (props.composeViewState.isSending) {
            return [sendingMenuItem.get()];
        } else {
            let barItems;
            if (isPublicFolderComposeViewState(composeViewState)) {
                barItems = [
                    sendMenuItem.get(),
                    attachMenuItem.get(),
                    protectMenuItem.get(),
                    discardMenuItem.get(),
                    showFromMenuItem.get(),
                    setImportanceMenuItem.get(),
                    switchBodyType.get(),
                    openAccCheckerMenuItem.get(),
                    openInPopoutMenuItem.get(),
                ];
            } else if (
                isFeatureEnabled('doc-attachInsert-linkIcon') ||
                isFeatureEnabled('doc-attachInsert-cloudLinkIcon')
            ) {
                barItems = [
                    customMenuItem.get(),
                    sendMenuItem.get(),
                    attachAsClassicMenuItem.get(),
                    insertLinksMenuItem.get(),
                    sensitivityMenuItem.get(),
                    protectMenuItem.get(),
                    discardMenuItem.get(),
                    saveDraftMenuItem.get(),
                    insertSignatureMenuItem.get(),
                    showFromMenuItem.get(),
                    protectMenuItemDropDown.get(),
                    setPermissionMenuItemDropDown.get(),
                    setImportanceMenuItem.get(),
                    showMessageOptionsMenuItem.get(),
                    switchBodyType.get(),
                    openAccCheckerMenuItem.get(),
                    openInPopoutMenuItem.get(),
                ];
            } else {
                barItems = [
                    customMenuItem.get(),
                    sendMenuItem.get(),
                    attachMenuItem.get(),
                    sensitivityMenuItem.get(),
                    protectMenuItem.get(),
                    discardMenuItem.get(),
                    saveDraftMenuItem.get(),
                    insertSignatureMenuItem.get(),
                    showFromMenuItem.get(),
                    protectMenuItemDropDown.get(),
                    setPermissionMenuItemDropDown.get(),
                    setImportanceMenuItem.get(),
                    showMessageOptionsMenuItem.get(),
                    switchBodyType.get(),
                    openAccCheckerMenuItem.get(),
                    openInPopoutMenuItem.get(),
                ];
            }
            return barItems;
        }
    });
    const customMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: 'customItem',
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.onRender]: () => {
                    // Always render item container since it's responsible for left padding
                    return (
                        <div style={{ paddingRight: props.leftPadding }}>
                            {props.renderCustomItem?.()}
                        </div>
                    );
                },
            });
        }
    );
    const sendingMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(sending),
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.onRender]: () => {
                    return (
                        <div className={styles.sendSpinner}>
                            <Spinner />
                            <span className={styles.sendSpinnerText}>{loc(sending)}</span>
                        </div>
                    );
                },
            });
        }
    );
    const sendMenuItem = useComputed(
        (): FilterableMenuItem => {
            const { composeViewState } = props;
            const disabled = composeViewState.uiEnabledState !== UIEnabledState.Enabled;
            let sendLabel: string;
            if (isPublicFolderComposeViewState(composeViewState)) {
                sendLabel = loc(composePostButton);
            } else {
                sendLabel = loc(composeSendButton);
            }
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: sendLabel,
                [FilterableMenuItemArgs.icon]: ControlIcons.Send,
                [FilterableMenuItemArgs.onClick]: () => {
                    send(composeViewState, targetWindow);
                },
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.disabled]: disabled,
            });
        }
    );
    const attachMenuItem = useComputed(
        (): FilterableMenuItem => {
            const composeViewState: ComposeViewState = props.composeViewState;
            const isSmimeCompose = isSmimeEnabledInViewState(composeViewState.smimeViewState);
            const attachmentMultiMenuEntriesClickHandler: AttachmentMultiMenuMap<() => void> = {
                computer: () => {
                    const datapoint = new PerformanceDatapoint('AttachLocalComputerClicked');
                    datapoint.addCustomData([AttachmentMenuEntrySource.CommandingBar]);
                    if (attachmentLocalComputerFilePicker.current.current) {
                        attachmentLocalComputerFilePicker.current.current.click();
                        datapoint.end();
                    } else {
                        datapoint.endWithError(
                            DatapointStatus.ClientError,
                            new Error('Lazy component not loaded')
                        );
                    }
                },
                onlineLocation: openFilePicker,
                uploadAndShareFromCloud: () => {
                    const datapoint = new PerformanceDatapoint('AttachUploadAndSharedClicked');
                    datapoint.addCustomData([AttachmentMenuEntrySource.CommandingBar]);
                    if (attachmentLocalComputerToCloudFilePicker.current.current) {
                        attachmentLocalComputerToCloudFilePicker.current.current.click();
                        datapoint.end();
                    } else {
                        datapoint.endWithError(
                            DatapointStatus.ClientError,
                            new Error('Lazy component not loaded')
                        );
                    }
                },
            };
            const attachmentMenus = Object.keys(attachmentMultiMenuEntry).reduce(
                (agg, key: keyof AttachmentMultiMenuEntry) => {
                    const attachmentMenuEntry = attachmentMultiMenuEntry[key];
                    const aria = attachmentMenuEntry.ariaLabel?.();
                    const menu = new FilterableMenuItem({
                        [FilterableMenuItemArgs.name]: attachmentMenuEntry.text(),
                        [FilterableMenuItemArgs.onClick]: async () => {
                            attachmentMultiMenuEntriesClickHandler[key]();
                            const getAttachmentSuggestionsTraceId = await lazyGetAttachmentSuggestionsTraceId.import();
                            const traceId = getAttachmentSuggestionsTraceId(
                                composeViewState.referenceItemId
                            );
                            attachmentMenuEntry.logUsage(
                                AttachmentMenuEntrySource.CommandingBar,
                                traceId
                            );
                        },
                    });
                    menu.iconProps = attachmentMenuEntry.iconProps;
                    menu.ariaLabel = aria;
                    menu.title = aria;
                    agg[key] = menu;
                    return agg;
                },
                {} as AttachmentMultiMenuMap<FilterableMenuItem>
            );
            const menuItems = [];
            const attachmentPolicy = getAttachmentPolicy();
            const classicAttachmentsEnabled = attachmentPolicy.ClassicAttachmentsEnabled;
            const referenceAttachmentsEnabled = attachmentPolicy.ReferenceAttachmentsEnabled;
            const referenceAttachmentsSupported =
                referenceAttachmentsEnabled &&
                getComposeUniqueState(composeViewState) === ComposeUniqueState.None;
            if (classicAttachmentsEnabled) {
                menuItems.push(attachmentMenus.computer);
            }
            if (!isSmimeCompose) {
                if (classicAttachmentsEnabled || referenceAttachmentsEnabled) {
                    menuItems.push(attachmentMenus.onlineLocation);
                }
                if (
                    attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled(
                        !!composeViewState.attachmentWell?.groupId
                    ) &&
                    referenceAttachmentsSupported
                ) {
                    menuItems.push(attachmentMenus.uploadAndShareFromCloud);
                }
                // Attachment suggestions (replyWith + MRU)
                const attachmentSuggestions = attachmentSuggestionsMenuItems.current?.getContextualMenuItems(
                    !isSmimeCompose,
                    composeViewState.referenceItemId,
                    referenceAttachmentsSupported,
                    true /* classicAttachmentsSupported */,
                    false /* shouldUseSuggetedFilesTitle */,
                    (suggestion: ReplyWithAttachmentSuggestion, suggestionIndex: number) => {
                        onAttachmentClicked(suggestion, suggestionIndex, composeViewState);
                    }
                );
                if (attachmentSuggestions) {
                    menuItems.push(...attachmentSuggestions);
                }
            }
            const menu: IContextualMenuProps = {
                items: menuItems,
                onMenuOpened: () => {
                    logAttachButtonClickedDatapoint(composeViewState.referenceItemId?.Id);

                    const referenceItem = getMailStore().items.get(
                        composeViewState.referenceItemId?.Id
                    );
                    attachmentSuggestionsMenuItems.current?.onMenuOpened(
                        !isSmimeCompose,
                        referenceItem,
                        composeViewState.composeId,
                        (isSmimeCompose
                            ? getSmimeAttachButtonsList()
                            : getDefaultButtonsList(!referenceAttachmentsSupported)
                        ).join(','),
                        referenceAttachmentsSupported,
                        true /* classicAttachmentsSupported */,
                        getSuggestedAttachmentMessageFromComposeViewState(composeViewState)
                    );
                },
                onMenuDismissed: () => {
                    attachmentSuggestionsMenuItems.current?.onMenuDismissed(
                        !isSmimeCompose,
                        composeViewState.referenceItemId,
                        referenceAttachmentsSupported,
                        true /* classicAttachmentsSupported */
                    );
                },
            };
            // If both classic and cloudy attachments are disabled then we disable the button
            const attachmentsDisabled =
                (!classicAttachmentsEnabled && !referenceAttachmentsEnabled) ||
                shouldDisable() ||
                isApprovalResponse(composeViewState);
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(attachTooltip),
                [FilterableMenuItemArgs.icon]: ControlIcons.Attach,
                [FilterableMenuItemArgs.disabled]: attachmentsDisabled,
                [FilterableMenuItemArgs.menuContent]: menu,
            });
        }
    );

    const attachAsClassicMenuItem = useComputed(
        (): FilterableMenuItem => {
            const { menuItems, onMenuOpened, onMenuDismissed } = getAttachAsClassicMenuItems(
                props.composeViewState,
                () => attachmentLocalComputerFilePickerWithInsertLink.current.current,
                () => attachmentSuggestionsMenuItems.current,
                targetWindow,
                true /*shouldPlaceSuggestionsInBottom */
            );
            const menu: IContextualMenuProps = {
                items: menuItems,
                onMenuOpened,
                onMenuDismissed,
            };
            // If classic is disabled then we disable the button
            const attachmentsDisabled =
                menuItems.length === 0 ||
                shouldDisable() ||
                isApprovalResponse(props.composeViewState);
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(attachTooltip),
                [FilterableMenuItemArgs.icon]: ControlIcons.Attach,
                [FilterableMenuItemArgs.disabled]: attachmentsDisabled,
                [FilterableMenuItemArgs.menuContent]: menu,
            });
        }
    );

    const insertLinksMenuItem = useComputed(
        (): FilterableMenuItem => {
            const { menuItems, onMenuOpened, onMenuDismissed } = getInsertLinkMenuItems(
                props.composeViewState,
                targetWindow,
                () => attachmentLocalFileToLinkPicker.current.current,
                () => attachmentSuggestionsMenuItems.current,
                true /*shouldPlaceSuggestionsInBottom */
            );
            const menu: IContextualMenuProps = {
                items: menuItems,
                onMenuOpened,
                onMenuDismissed,
            };
            // If no valid context menu items then we disable the button
            const insertDisabled =
                menuItems.length === 0 ||
                shouldDisable() ||
                isApprovalResponse(props.composeViewState);
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.onRender]: () => {
                    return <LazyInsertMenuItem menu={menu} disabled={insertDisabled} />;
                },
            });
        }
    );

    const sensitivityMenuItem = useComputed(
        (): FilterableMenuItem => {
            const composeViewState: ComposeViewState = props.composeViewState;
            const isSmimeCompose = isSmimeEnabledInViewState(composeViewState.smimeViewState);
            let { clpLabels } = protectionStore;
            let menu = getSensitivitySubMenu(
                clpLabels,
                composeViewState,
                isSmimeCompose,
                targetWindow
            );
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(sensitivityMenuText),
                [FilterableMenuItemArgs.icon]: ControlIcons.MarkAsProtected,
                [FilterableMenuItemArgs.shouldShow]: () => isCLPEnabled(),
                [FilterableMenuItemArgs.disabled]: shouldDisable(),
                [FilterableMenuItemArgs.menuContent]: menu,
            });
        }
    );
    const protectMenuItemDropDown = useComputed(
        (): FilterableMenuItem => {
            // There are two variables controls the dropdown: CLPEnabled, RmsV2UIEnabled
            // 1. CLPEnabled=true and RmsV2UIEabled=true/false, hide "Encrypt" button and drop down has two buttons (encrypt only and DNF)
            // 2. CLPEnabled=false and RmsV2UIEnabled=true, hide drop down and show "Encrypt" button.
            // 3. CLPEnabled=false and RmsV2UIEnabled=false, hide "Encrypt" button and dropdown has all RMS templates.
            // Note: if the user does not have any RMS template and message classification available, then the
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(composeProtectButton),
                [FilterableMenuItemArgs.shouldShow]: () => {
                    return (
                        isEncryptionEnabled() &&
                        isCLPEnabled() &&
                        isEncryptionTemplateAvailable([ENCRYPT_GUID, ENCRYPT_DNF_GUID])
                    );
                },
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]:
                    shouldDisableEncryptionButton(props.composeViewState.protectionViewState) ||
                    shouldDisable(),
                [FilterableMenuItemArgs.menuContent]: getEncryptionDropDownSubMenu(
                    props.composeViewState,
                    [ENCRYPT_GUID, ENCRYPT_DNF_GUID]
                ),
            });
        }
    );
    const setPermissionMenuItemDropDown = useComputed(
        (): FilterableMenuItem => {
            const { RmsV2UIEnabled } = getUserConfiguration();
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(composeSetPermissionDropDown),
                [FilterableMenuItemArgs.shouldShow]: () => {
                    return (
                        isEncryptionEnabled() && !RmsV2UIEnabled && isEncryptionTemplateAvailable()
                    );
                },
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]:
                    shouldDisableEncryptionButton(props.composeViewState.protectionViewState) &&
                    protectionStore.messageClassifications.size == 0, // This button should be disabled only when both IRMTemplate and MessageClassification are empty.
                [FilterableMenuItemArgs.menuContent]: getIRMTemplatesDropDown(
                    props.composeViewState
                ),
            });
        }
    );
    const protectMenuItem = useComputed(
        (): FilterableMenuItem => {
            const { composeViewState } = props;
            const { protectionViewState } = composeViewState;
            const shouldDisableEncryption =
                shouldDisableEncryptionButton(protectionViewState) || shouldDisable();
            const { RmsV2UIEnabled } = getUserConfiguration();
            if (isConsumer()) {
                const encryptMenu = new FilterableMenuItem({
                    [FilterableMenuItemArgs.name]: loc(encryptMenuText),
                    [FilterableMenuItemArgs.onClick]: () => {
                        protectMessageAndRemoveAddin(composeViewState, ENCRYPT_GUID);
                    },
                    [FilterableMenuItemArgs.disabled]: shouldDisableEncryptionButton(
                        protectionViewState,
                        ENCRYPT_GUID
                    ),
                });
                const dnfMenu = new FilterableMenuItem({
                    [FilterableMenuItemArgs.name]: loc(encryptDisableForwardingMenuText),
                    [FilterableMenuItemArgs.onClick]: () => {
                        protectMessageAndRemoveAddin(composeViewState, ENCRYPT_DNF_GUID);
                    },
                    [FilterableMenuItemArgs.disabled]: shouldDisableEncryptionButton(
                        protectionViewState,
                        ENCRYPT_DNF_GUID
                    ),
                });
                return new FilterableMenuItem({
                    [FilterableMenuItemArgs.name]: loc(composeProtectButton),
                    [FilterableMenuItemArgs.icon]: SurfaceActionIcons.Encryption,
                    [FilterableMenuItemArgs.shouldShow]: () => {
                        return (
                            isEncryptionEnabled() &&
                            !shouldDisableEncryption &&
                            isEncryptionTemplateAvailable()
                        );
                    },
                    [FilterableMenuItemArgs.disabled]: shouldDisableEncryption,
                    [FilterableMenuItemArgs.menuContent]: [encryptMenu, dnfMenu],
                });
            } else {
                return new FilterableMenuItem({
                    [FilterableMenuItemArgs.name]: loc(composeProtectButton),
                    [FilterableMenuItemArgs.icon]: isCLPEnabled()
                        ? null
                        : SurfaceActionIcons.Encryption,
                    [FilterableMenuItemArgs.onClick]: () => {
                        protectMessageAndRemoveAddin(composeViewState, IRM_DEFAULT_GUID);
                    },
                    [FilterableMenuItemArgs.shouldShow]: () => {
                        // When CLP is enabled the "Encrypt" button should be hidden
                        // When RmsV2UIEnabled is false, "Encrypt" button should be hidden
                        return (
                            isEncryptionEnabled() &&
                            !isCLPEnabled() &&
                            !shouldDisableEncryption &&
                            RmsV2UIEnabled &&
                            isEncryptionTemplateAvailable()
                        );
                    },
                    [FilterableMenuItemArgs.displayOption]: isCLPEnabled()
                        ? DisplayOption.Overflow
                        : null,
                    [FilterableMenuItemArgs.disabled]: shouldDisableEncryption,
                });
            }
        }
    );
    const discardMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(composeDiscardButton),
                [FilterableMenuItemArgs.icon]: ControlIcons.Delete,
                [FilterableMenuItemArgs.onClick]: () => {
                    discardCompose(props.composeViewState, targetWindow);
                },
            });
        }
    );
    const saveDraftMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(saveDraft),
                [FilterableMenuItemArgs.onClick]: () => {
                    trySaveMessage(props.composeViewState);
                    focusToEditor(props.composeViewState);
                },
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]: shouldDisable(),
            });
        }
    );
    const openInPopoutMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.icon]: ControlIcons.OpenInNewWindow,
                [FilterableMenuItemArgs.onClick]: () => {
                    popoutCompose(props.composeViewState);
                },
                [FilterableMenuItemArgs.shouldShow]: () => {
                    // 1. isDeepLink() == false => This is not a deeplink window
                    // 2. targetWindow == window => This is not a projection window
                    return !isDeepLink() && targetWindow === window;
                },
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Right,
                [FilterableMenuItemArgs.disabled]: shouldDisable(),
                [FilterableMenuItemArgs.ariaLabel]: loc(popoutsOpenInNewWindow),
                [FilterableMenuItemArgs.title]: loc(popoutsOpenInNewWindow),
            });
        }
    );
    const insertSignatureMenuItem = useComputed(
        (): FilterableMenuItem => {
            if (isRoamingSignatureEnabled()) {
                const insertSignatureDropdown = getInsertSignatureDropdown(props.composeViewState);
                return new FilterableMenuItem({
                    [FilterableMenuItemArgs.name]: loc(insertSignature_1),
                    [FilterableMenuItemArgs.onClick]: () => {
                        // if there is no roaming signature fallback to legacy signature
                        if (!insertSignatureDropdown || insertSignatureDropdown.length == 0) {
                            insertSignature(props.composeViewState);
                        }
                    },
                    [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                    [FilterableMenuItemArgs.disabled]: shouldDisable(),
                    [FilterableMenuItemArgs.menuContent]: insertSignatureDropdown,
                });
            } else {
                return new FilterableMenuItem({
                    [FilterableMenuItemArgs.name]: loc(insertSignature_1),
                    [FilterableMenuItemArgs.onClick]: () => {
                        const datapoint = new PerformanceDatapoint('ComposeCommandInsertSignature');
                        insertSignature(props.composeViewState);
                        datapoint.end();
                    },
                    [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                    [FilterableMenuItemArgs.disabled]: shouldDisable(),
                });
            }
        }
    );
    const showFromMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: !props.composeViewState.fromViewState.isFromShown
                    ? loc(showFrom)
                    : loc(hideFrom),
                [FilterableMenuItemArgs.onClick]: () => {
                    toggleFrom(props.composeViewState);
                    focusToEditor(props.composeViewState);
                },
                [FilterableMenuItemArgs.shouldShow]: () => {
                    return !props.composeViewState.fromViewState.isFromReadOnly;
                },
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]:
                    isSmimeEnabledInViewState(props.composeViewState.smimeViewState) ||
                    shouldDisable(),
            });
        }
    );
    const setImportanceMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(setImportance_1),
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]: shouldDisable(),
                [FilterableMenuItemArgs.menuContent]: [
                    getSetImportanceMenuSubItem(loc(highImportanceMenuText), 'High'),
                    getSetImportanceMenuSubItem(loc(normalImportanceMenuText), 'Normal'),
                    getSetImportanceMenuSubItem(loc(lowImportanceMenuText), 'Low'),
                ],
            });
        }
    );
    const showMessageOptionsMenuItem = useComputed(
        (): FilterableMenuItem => {
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(showMessageOptions),
                [FilterableMenuItemArgs.onClick]: () => {
                    showMessageOptionsDialog(props.composeViewState, targetWindow);
                },
                [FilterableMenuItemArgs.shouldShow]: () => {
                    return isMessageOptionsEnabled();
                },
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]: shouldDisable(),
            });
        }
    );
    const switchBodyType = useComputed(
        (): FilterableMenuItem => {
            const { composeViewState } = props;
            const isPlainText = composeViewState.bodyType != 'HTML';
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: isPlainText
                    ? loc(switchToHTMLMenuItem)
                    : loc(switchToPlainTextMenuItem),
                [FilterableMenuItemArgs.onClick]: () =>
                    switchBodytype(composeViewState, targetWindow),
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]: shouldDisable(),
            });
        }
    );
    const openAccCheckerMenuItem = useComputed(
        (): FilterableMenuItem => {
            const { composeViewState } = props;
            const isPlainText = composeViewState.bodyType != 'HTML';
            return new FilterableMenuItem({
                [FilterableMenuItemArgs.name]: loc(accCheckerMenuItemText),
                [FilterableMenuItemArgs.onClick]: openAccChecker,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Overflow,
                [FilterableMenuItemArgs.disabled]: isPlainText || shouldDisable(),
                [FilterableMenuItemArgs.checked]: composeViewState.accChecker.isOpen,
                [FilterableMenuItemArgs.canCheck]: true,
            });
        }
    );
    const createAttachmentLocalComputerFilePicker = (
        getCreateAttachmentHandler: GetCreateAttachmentHandlerFunc,
        ref: React.Ref<IAttachmentLocalComputerFilePicker>
    ) => {
        return (
            <AttachmentLocalComputerFilePicker
                ref={ref}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.Attach}
                createAttachmentHandler={getCreateAttachmentHandler(
                    props.composeViewState.attachmentWell,
                    (files, properties, dataProvider) => {
                        aFPCreateAttachmentHandler(
                            files,
                            props.composeViewState,
                            properties,
                            dataProvider
                        );
                    },
                    getComposeUniqueState(props.composeViewState),
                    targetWindow
                )}
            />
        );
    };
    const openFilePicker = () => {
        const { composeViewState } = props;
        const startTime: OwaDate = owaDate();
        clickAttachButton(
            false,
            params => {
                logUsage(
                    'FilePickerSelectedFiles',
                    { asLink: false },
                    {
                        cosmosOnlyData: JSON.stringify({
                            timeSpan: differenceInMilliseconds(owaDate(), startTime),
                        }),
                    }
                );
                const baseProperties = { isInline: params.isInline, isHiddenInline: false };
                const properties: CreateAttachmentsProperties = params.shouldShare
                    ? { ...baseProperties, shouldShare: true }
                    : { ...baseProperties, shouldShare: false };
                aFPCreateAttachmentHandler(
                    params.files,
                    composeViewState,
                    properties,
                    params.dataProviderType
                );
            },
            async params => {
                const insertLinks = await lazyInsertLinks.import();
                insertLinks(
                    params.files,
                    composeViewState,
                    composeViewState,
                    getCurrentFromAddress(composeViewState.toRecipientWell),
                    targetWindow
                );
            },
            composeViewState,
            targetWindow
        );
    };
    const getSetImportanceMenuSubItem = (
        menuText: string,
        importance: ImportanceType
    ): FilterableMenuItem => {
        const { composeViewState } = props;
        return new FilterableMenuItem({
            [FilterableMenuItemArgs.name]: menuText,
            [FilterableMenuItemArgs.onClick]: () => {
                setImportance(composeViewState, importance);
                focusToEditor(composeViewState);
            },
            [FilterableMenuItemArgs.checked]: composeViewState.importance == importance, // checked
            [FilterableMenuItemArgs.canCheck]: true,
        });
    };
    const openAccChecker = () => {
        const { composeViewState } = props;
        const { accChecker } = composeViewState;
        if (accChecker.isOpen) {
            lazyCloseChecker.import().then(closeChecker => closeChecker(accChecker, targetWindow));
        } else {
            lazyOpenChecker
                .import()
                .then(openChecker => openChecker(accChecker, composeViewState, targetWindow));
        }
    };
    const shouldDisable = () => {
        return props.composeViewState.uiEnabledState === UIEnabledState.AllDisabled;
    };

    const handlerForUploadAndInsertLinks = attachmentMultiMenuEntry.uploadAndShareFromCloud.getCreateAttachmentHandler(
        props.composeViewState.attachmentWell,
        (files, properties, dataProvider) =>
            upsellAndInsertLinks(
                files,
                props.composeViewState,
                properties,
                dataProvider,
                targetWindow
            ),
        getComposeUniqueState(props.composeViewState),
        targetWindow
    );

    return (
        <span>
            {createAttachmentLocalComputerFilePicker(
                attachmentMultiMenuEntry.computer.getCreateAttachmentHandler,
                attachmentLocalComputerFilePicker.current
            )}

            {createAttachmentLocalComputerFilePicker(
                attachmentMultiMenuEntry.uploadAndShareFromCloud.getCreateAttachmentHandler,
                attachmentLocalComputerToCloudFilePicker.current
            )}
            <AttachmentLocalComputerFilePicker
                ref={attachmentLocalFileToLinkPicker.current}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.InsertAsLink}
                createAttachmentHandler={handlerForUploadAndInsertLinks}
            />
            <AttachmentLocalComputerFilePicker
                ref={attachmentLocalComputerFilePickerWithInsertLink.current}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.Attach}
                createAttachmentHandler={getCreateClassicAttachmentHandler(
                    props.composeViewState,
                    targetWindow,
                    true
                )}
            />
            <FilterableCommandBar
                className={classNames(styles.filterableCommandBar)}
                filterableItems={commandingBarItems.get()}
                elipisisAriaLabel={loc(composeEllipsisAriaLabelText)}
            />
        </span>
    );
});
